// Track B PR3 — per-batch progress table.
//
// Renders one row per `batch_ordinal` observed across RAW / TAGGED / CLEANED
// detail items for a conversation. Columns:
//
//   [ # | Transcribed | Tagged | Cleaned ]
//
// Each cell is one of four states:
//
//   - "idle"     — no event yet for this (batch, stage). Grey.
//   - "running"  — start event fired but no completion yet. Spinner.
//   - "done"     — completion event fired (or REST hydrate found the item).
//                  Green check.
//   - "failed"   — failure event fired. Red badge with payload.error tooltip.
//
// State sources
// -------------
// 1. Initial hydration: `GET /api/v1/conversations/{id}/detail_items?type=...`
//    for each of RAW / TAGGED / CLEANED. Any returned item bumps the
//    corresponding cell to "done" and adds the row if it doesn't exist.
//
// 2. SSE event updates (passed in via the `events` prop or by calling
//    `applyEvent`). Mapping per `docs/plan-thread-ui.md`:
//
//       batch_transcription_started   → Transcribed = running
//       raw_transcript_emitted        → Transcribed = done
//       batch_transcription_complete  → Transcribed = done (alias)
//       batch_tagged_started          → Tagged      = running
//       tagged_transcript_ready       → Tagged      = done
//       batch_tagged_failed           → Tagged      = failed
//       batch_cleaned_started         → Cleaned     = running
//       cleaned_transcript_ready      → Cleaned     = done
//       batch_cleaned_failed          → Cleaned     = failed
//
// The reducer is exported so ThreadDetailPage can share the same logic
// when it applies SSE events at the page level (per-conversation slice).

import React, { useEffect, useReducer, useMemo, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { getConversationDetailItems } from "../services/embed";

export const TRANSCRIPT_TYPES = [
  "RAW_TRANSCRIPT",
  "TAGGED_TRANSCRIPT",
  "CLEANED_TRANSCRIPT",
];

const STAGE_COLUMNS = [
  { key: "transcribed", label: "Transcribed" },
  { key: "tagged", label: "Tagged" },
  { key: "cleaned", label: "Cleaned" },
];

// Detail-item type → stage column key. RAW = transcribed; TAGGED = tagged;
// CLEANED = cleaned.
const TYPE_TO_STAGE = {
  RAW_TRANSCRIPT: "transcribed",
  TAGGED_TRANSCRIPT: "tagged",
  CLEANED_TRANSCRIPT: "cleaned",
};

// SSE event-type → (stage, cellState[, errorFrom]).
// errorFrom = key to pull from event.payload for tooltip text on failures.
const EVENT_MAP = {
  batch_transcription_started: { stage: "transcribed", state: "running" },
  batch_transcription_complete: { stage: "transcribed", state: "done" },
  raw_transcript_emitted: { stage: "transcribed", state: "done" },
  batch_tagged_started: { stage: "tagged", state: "running" },
  batch_tagged_failed: { stage: "tagged", state: "failed", errorFrom: "error" },
  tagged_transcript_ready: { stage: "tagged", state: "done" },
  batch_cleaned_started: { stage: "cleaned", state: "running" },
  batch_cleaned_failed: { stage: "cleaned", state: "failed", errorFrom: "error" },
  cleaned_transcript_ready: { stage: "cleaned", state: "done" },
};

// State transition policy: "done" and "failed" are sticky against subsequent
// "running" events for the same cell. A retry that re-emits "started" will
// not yank a completed cell back to a spinner.
function shouldOverwrite(prev, next) {
  if (prev === next) return false;
  if (prev === "done" || prev === "failed") {
    // Allow failed → done (a retry recovered) and done → failed
    // (post-hoc failure) — but ignore demotion back to "running".
    return next !== "running";
  }
  return true;
}

const EMPTY_CELL = { state: "idle", error: null };

function ensureBatch(batches, ordinal) {
  if (batches[ordinal]) return batches[ordinal];
  return {
    transcribed: { ...EMPTY_CELL },
    tagged: { ...EMPTY_CELL },
    cleaned: { ...EMPTY_CELL },
  };
}

/**
 * Reducer for the batch table. Exported so other surfaces (ThreadDetailPage's
 * page-level useReducer) can re-use the same SSE → cell logic.
 *
 * Action shapes:
 *   - { type: "hydrate", items: ConversationDetailItem[] }
 *       items must include `index` (the batch ordinal) and `type` (one of
 *       RAW_TRANSCRIPT / TAGGED_TRANSCRIPT / CLEANED_TRANSCRIPT). Items
 *       outside the three transcript types are ignored.
 *   - { type: "event", event: { type, payload } }
 *       a single SSE envelope. The reducer pulls the batch ordinal from
 *       `event.payload.batch_ordinal`. Missing ordinals are no-ops.
 */
export function batchTableReducer(state, action) {
  if (action.type === "hydrate") {
    const next = { ...state };
    for (const item of action.items || []) {
      const stage = TYPE_TO_STAGE[item?.type];
      if (!stage) continue;
      const ord = item?.index;
      if (ord == null) continue;
      const batch = { ...ensureBatch(next, ord) };
      batch[stage] = { state: "done", error: null };
      next[ord] = batch;
    }
    return next;
  }
  if (action.type === "event") {
    const ev = action.event || {};
    const map = EVENT_MAP[ev.type];
    if (!map) return state;
    const ord = ev?.payload?.batch_ordinal;
    if (ord == null) return state;
    const batch = { ...ensureBatch(state, ord) };
    const prevCell = batch[map.stage] || EMPTY_CELL;
    if (!shouldOverwrite(prevCell.state, map.state)) return state;
    const error =
      map.state === "failed" && map.errorFrom
        ? ev?.payload?.[map.errorFrom] || null
        : null;
    batch[map.stage] = { state: map.state, error };
    return { ...state, [ord]: batch };
  }
  if (action.type === "reset") {
    return {};
  }
  return state;
}

const initialState = {};

const CellView = ({ cell }) => {
  switch (cell.state) {
    case "running":
      return (
        <Tooltip label="In progress" hasArrow>
          <Box display="inline-block">
            <Spinner size="xs" />
          </Box>
        </Tooltip>
      );
    case "done":
      return (
        <Tooltip label="Done" hasArrow>
          <Box
            color="green.500"
            display="inline-flex"
            alignItems="center"
            data-cell-state="done"
          >
            <CheckIcon boxSize={3} />
          </Box>
        </Tooltip>
      );
    case "failed":
      return (
        <Tooltip
          label={cell.error || "Failed (no error message)"}
          hasArrow
        >
          <Badge colorScheme="red" data-cell-state="failed">
            failed
          </Badge>
        </Tooltip>
      );
    case "idle":
    default:
      return (
        <Box
          as="span"
          color="gray.300"
          fontSize="xs"
          data-cell-state="idle"
        >
          —
        </Box>
      );
  }
};

/**
 * BatchProgressTable
 *
 * Props:
 *   - conversationId : string | number — used to hydrate the initial state
 *                       from `GET /conversations/{id}/detail_items`.
 *   - events         : array of SSE envelope objects (optional, useful when
 *                       the parent maintains its own SSE stream and just
 *                       feeds events down). Each new event applied via a
 *                       reducer dispatch.
 *   - applyEventRef  : optional `{ current: (event) => void }` — populated
 *                       by this component so the parent can imperatively
 *                       call `applyEventRef.current(event)` when an SSE
 *                       envelope arrives. Avoids forcing the parent to
 *                       re-render the whole prop array.
 *   - fetchDetailItemsFn : injection seam for tests. Defaults to
 *                       `getConversationDetailItems`.
 */
const BatchProgressTable = ({
  conversationId,
  events,
  applyEventRef,
  fetchDetailItemsFn = getConversationDetailItems,
}) => {
  const [state, dispatch] = useReducer(batchTableReducer, initialState);

  // Expose `applyEvent` via ref for parents that subscribe to SSE at a
  // higher level (ThreadDetailPage).
  const applyEvent = useCallback((event) => {
    dispatch({ type: "event", event });
  }, []);
  useEffect(() => {
    if (applyEventRef) applyEventRef.current = applyEvent;
  }, [applyEventRef, applyEvent]);

  // Layered hydration: fetch RAW, TAGGED, CLEANED in parallel; dispatch
  // a single hydrate action so the reducer sees them all at once.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    dispatch({ type: "reset" });
    (async () => {
      try {
        const responses = await Promise.all(
          TRANSCRIPT_TYPES.map((type) =>
            fetchDetailItemsFn(conversationId, { type }).catch(() => ({
              items: [],
            })),
          ),
        );
        if (cancelled) return;
        const allItems = [];
        responses.forEach((resp, i) => {
          const items =
            resp?.items ||
            (Array.isArray(resp) ? resp : null) ||
            [];
          for (const it of items) {
            allItems.push({ ...it, type: TRANSCRIPT_TYPES[i] });
          }
        });
        dispatch({ type: "hydrate", items: allItems });
      } catch (_e) {
        // Hydrate failures are non-fatal — SSE events still drive the table.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, fetchDetailItemsFn]);

  // Optional events-prop path (for callers that want a declarative array
  // of events instead of the imperative applyEventRef).
  useEffect(() => {
    if (!Array.isArray(events) || events.length === 0) return;
    for (const ev of events) {
      dispatch({ type: "event", event: ev });
    }
  }, [events]);

  const ordinals = useMemo(
    () =>
      Object.keys(state)
        .map((s) => Number(s))
        .sort((a, b) => a - b),
    [state],
  );

  return (
    <Box data-testid="batch-progress-table">
      <Heading size="sm" mb={2}>
        Batch progress
      </Heading>
      {ordinals.length === 0 ? (
        <Box
          py={6}
          textAlign="center"
          bg="gray.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          borderStyle="dashed"
          data-testid="batch-progress-empty"
        >
          <Text color="gray.500" fontSize="xs">
            No batches yet. Cells will fill in as the pipeline runs.
          </Text>
        </Box>
      ) : (
        <Box
          overflowX="auto"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <Th
                  py={2}
                  px={3}
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="gray.500"
                >
                  #
                </Th>
                {STAGE_COLUMNS.map((c) => (
                  <Th
                    key={c.key}
                    py={2}
                    px={3}
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.500"
                  >
                    {c.label}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {ordinals.map((ord) => {
                const batch = state[ord];
                return (
                  <Tr
                    key={ord}
                    data-testid={`batch-row-${ord}`}
                    borderTopWidth="1px"
                    borderTopColor="gray.100"
                  >
                    <Td py={2} px={3}>
                      <Text fontSize="xs" fontFamily="mono" color="gray.500">
                        {ord}
                      </Text>
                    </Td>
                    {STAGE_COLUMNS.map((c) => (
                      <Td
                        key={c.key}
                        py={2}
                        px={3}
                        data-testid={`batch-cell-${ord}-${c.key}`}
                      >
                        <CellView cell={batch[c.key]} />
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default BatchProgressTable;
